import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Trash2 as DeleteIcon } from 'lucide-react';
import { Button, Chip, ShellHeader } from '@coordio/ui';
import { FormSection, InlineErrorPanel, LoadingSkeleton, PageContainer } from '@coordio/ui/layout';
import { memoryService } from '../services/memoryService';
import { useNotificationStore } from '../store/notificationStore';
import { useConfirmDialog } from '../components/common/ConfirmDialog';
import { MemoryScope, MemoryType, type AgentMemoryEntryDto } from '../types/memory';

const scopeLabel = (scope: number) => {
  switch (scope) {
    case MemoryScope.Session:
      return 'Session';
    case MemoryScope.Project:
      return 'Project';
    case MemoryScope.Tenant:
      return 'Tenant';
    default:
      return 'Unknown';
  }
};

const typeLabel = (type: number) => {
  switch (type) {
    case MemoryType.Fact:
      return 'Fact';
    case MemoryType.Decision:
      return 'Decision';
    case MemoryType.Outcome:
      return 'Outcome';
    case MemoryType.Preference:
      return 'Preference';
    default:
      return 'Unknown';
  }
};

/**
 * Memory detail page component.
 */
export const MemoryDetailPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { addNotification } = useNotificationStore();
  const confirm = useConfirmDialog();

  const [memory, setMemory] = useState<AgentMemoryEntryDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const load = async () => {
      if (!id) return;
      setLoading(true);
      setError(null);
      try {
        const m = await memoryService.getMemory(id);
        setMemory(m);
      } catch (err: any) {
        const msg = err.response?.data?.message || 'Failed to load memory';
        setError(msg);
        addNotification(msg, 'error');
      } finally {
        setLoading(false);
      }
    };

    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const handleDelete = async () => {
    if (!id) return;
    const ok = await confirm({
      title: 'Delete memory',
      message: 'Delete this memory entry?',
      confirmText: 'Delete',
    });
    if (!ok) return;
    try {
      await memoryService.deleteMemory(id);
      addNotification('Memory deleted', 'success');
      navigate('/memories');
    } catch (err: any) {
      const msg = err.response?.data?.message || 'Failed to delete memory';
      addNotification(msg, 'error');
    }
  };

  return (
    <>
      <ShellHeader
        title="Memory Details"
        description="View stored memory entry content and metadata."
        data-testid="memory-header"
        actions={
          <>
            <Button variant="outline" onClick={() => navigate('/memories')} data-testid="back-memory-btn">
              Memories
            </Button>
            {memory && (
              <Button variant="destructive" onClick={handleDelete} data-testid="delete-memory-btn">
                <DeleteIcon /> Delete
              </Button>
            )}
          </>
        }
      />

      <PageContainer data-testid="memory-form-page">
        {error && <InlineErrorPanel message={error} data-testid="memory-error-panel" action={<Button variant="ghost" size="sm" onClick={() => setError(null)}>Dismiss</Button>} />}

        {loading && <LoadingSkeleton rows={6} data-testid="memory-loading" />}

        {!loading && memory && (
          <>
            <div className="flex flex-wrap gap-1 mb-3">
              <Chip label={`Scope: ${scopeLabel(memory.scope)}`} />
              <Chip label={`Type: ${typeLabel(memory.type)}`} />
              <Chip color="accent" label={`Importance: ${memory.importanceScore.toFixed(2)}`} />
              <Chip variant="outlined" label={`Accesses: ${memory.accessCount}`} />
              {memory.expiresAt && (
                <Chip color="amber" label={`Expires: ${new Date(memory.expiresAt).toLocaleString()}`} />
              )}
            </div>

            {memory.summary && (
              <FormSection title="Summary" data-testid="memory-section-summary">
                <p className="text-sm">{memory.summary}</p>
              </FormSection>
            )}

            <FormSection title="Content" data-testid="memory-section-content">
              <p className="text-sm whitespace-pre-wrap">
                {memory.content}
              </p>
            </FormSection>

            {memory.tags && memory.tags.length > 0 && (
              <FormSection title="Tags" data-testid="memory-section-tags">
                <div className="flex flex-wrap gap-1">
                  {memory.tags.map((t) => (
                    <Chip key={t} className="text-xs" label={t} />
                  ))}
                </div>
              </FormSection>
            )}

            {memory.metadataJson && (
              <FormSection title="Metadata (JSON)" data-testid="memory-section-metadata">
                <pre
                  className="whitespace-pre-wrap m-0 font-mono text-sm overflow-auto"
                >
                  {memory.metadataJson}
                </pre>
              </FormSection>
            )}
          </>
        )}
      </PageContainer>
    </>
  );
};
